import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import {
  FaCheck,
  FaTimes,
  FaDiscord,
  FaSun,
  FaCloud,
  FaStar,
  FaHandHoldingUsd,
  FaCreditCard,
  FaQrcode,
  FaCopy,
} from 'react-icons/fa';
import Navbar from '../../components/Navbar/Navbar';
import discordService from '../../services/discordService';
import { createCheckoutSession, validatePromoCode, fetchVipPricing } from '../../services/stripeService';
import { createPixPayment, getPixPaymentStatus, simulatePixPayment } from '../../services/mercadoPagoService';
import { isValidMinecraftNick } from '../../config/vipPlans';
import { useI18n } from '../../i18n';
import './VIP.css';

const SupportWidget = lazy(() =>
  import('../../components/SupportChat/SupportWidget')
);

const CELEBRATION_COLORS = ['#d4a017', '#f0d078', '#7c3aed', '#a78bfa', '#6366f1', '#f3efff', '#34d399'];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const fireVipConfetti = () => {
  if (prefersReducedMotion()) return;

  const defaults = {
    colors: CELEBRATION_COLORS,
    disableForReducedMotion: true,
    zIndex: 3100,
  };

  confetti({
    ...defaults,
    particleCount: 120,
    spread: 100,
    startVelocity: 45,
    origin: { y: 0.65 },
  });

  confetti({
    ...defaults,
    particleCount: 70,
    angle: 60,
    spread: 70,
    origin: { x: 0, y: 0.7 },
  });

  confetti({
    ...defaults,
    particleCount: 70,
    angle: 120,
    spread: 70,
    origin: { x: 1, y: 0.7 },
  });

  window.setTimeout(() => {
    confetti({
      ...defaults,
      particleCount: 90,
      spread: 140,
      startVelocity: 35,
      scalar: 1.1,
      origin: { y: 0.4 },
    });
  }, 280);

  window.setTimeout(() => {
    confetti({
      ...defaults,
      particleCount: 50,
      spread: 360,
      startVelocity: 22,
      ticks: 180,
      gravity: 0.85,
      origin: { x: 0.5, y: 0.45 },
    });
  }, 650);
};

/**
 * Ordem fixa em todos os planos (mesma linha = mesma vantagem).
 * Progressão: base → Origin → Eclipse → Investidor.
 */
const allFeatures = [
  'Acesso ao servidor',
  'Pontos de home',
  'Blocos de proteção',
  'Anúncios no leilão',
  'Taxa do banco (PIX)',
  'Cofres no banco',
  'Prefixo no chat',
  'Cores e formatação no chat',
  'Canal VIP',
  'Gradiente na tag do clã',
  'Comandos utilitários',
  'Efeitos de partícula',
  'Salário diário',
  'Prioridade na fila',
  'Desconto no baú do clã',
  'Kit exclusivo',
  'Acesso antecipado a eventos',
  'Suporte prioritário',
];

const plans = [
  {
    id: 'free',
    name: 'Grátis',
    tagline: 'Comece a aventura',
    accent: 'stone',
    icon: <FaStar />,
    price: { monthly: 0, annual: 0 },
    features: {
      'Acesso ao servidor': true,
      'Pontos de home': { value: '1 home', has: true },
      'Prefixo no chat': false,
      'Cores e formatação no chat': false,
      'Canal VIP': false,
      'Blocos de proteção': { value: '5.000 blocos · 100/h', has: true },
      'Anúncios no leilão': { value: '3 anúncios leilão', has: true },
      'Taxa do banco (PIX)': { value: 'Taxa PIX 10%', has: true },
      'Cofres no banco': false,
      'Desconto no baú do clã': false,
      'Gradiente na tag do clã': false,
      'Comandos utilitários': false,
      'Efeitos de partícula': false,
      'Salário diário': false,
      'Kit exclusivo': false,
      'Prioridade na fila': false,
      'Acesso antecipado a eventos': false,
      'Suporte prioritário': false,
    },
    highlighted: false,
  },
  {
    id: 'origin',
    name: 'Origin',
    tagline: 'O equilíbrio perfeito',
    accent: 'violet',
    icon: <FaSun />,
    price: { monthly: 10, annual: 96 },
    features: {
      'Acesso ao servidor': true,
      'Pontos de home': { value: '2 homes', has: true },
      'Prefixo no chat': { value: 'Tag [Origin]', has: true },
      'Cores e formatação no chat': true,
      'Canal VIP': true,
      'Blocos de proteção': { value: '10.000 blocos · 150/h', has: true },
      'Anúncios no leilão': { value: '5 anúncios leilão', has: true },
      'Taxa do banco (PIX)': { value: 'Taxa PIX 10%', has: true },
      'Cofres no banco': { value: '3 cofres', has: true },
      'Desconto no baú do clã': false,
      'Gradiente na tag do clã': true,
      'Comandos utilitários': { value: '/craft', has: true },
      'Efeitos de partícula': { value: '1 efeito', has: true },
      'Salário diário': { value: 'Salário 1.000/dia', has: true },
      'Kit exclusivo': false,
      'Prioridade na fila': true,
      'Acesso antecipado a eventos': false,
      'Suporte prioritário': false,
    },
    highlighted: true,
  },
  {
    id: 'eclipse',
    name: 'Eclipse',
    tagline: 'Poder de sobra',
    accent: 'indigo',
    icon: <FaCloud />,
    price: { monthly: 20, annual: 192 },
    features: {
      'Acesso ao servidor': true,
      'Pontos de home': { value: '5 homes', has: true },
      'Prefixo no chat': { value: 'Tag [Eclipse]', has: true },
      'Cores e formatação no chat': true,
      'Canal VIP': true,
      'Blocos de proteção': { value: '20.000 blocos · 200/h', has: true },
      'Anúncios no leilão': { value: '10 anúncios leilão', has: true },
      'Taxa do banco (PIX)': { value: 'Taxa PIX 5%', has: true },
      'Cofres no banco': { value: '4 cofres · juros +0,2%', has: true },
      'Desconto no baú do clã': { value: '20% no baú do clã', has: true },
      'Gradiente na tag do clã': true,
      'Comandos utilitários': { value: '/craft e /ec', has: true },
      'Efeitos de partícula': { value: '2 efeitos', has: true },
      'Salário diário': { value: 'Salário 2.500/dia', has: true },
      'Kit exclusivo': { value: 'Kit mensal', has: true },
      'Prioridade na fila': true,
      'Acesso antecipado a eventos': true,
      'Suporte prioritário': false,
    },
    highlighted: false,
  },
  {
    id: 'investidor',
    name: 'Investidor',
    tagline: 'Patrocine o Nexus',
    accent: 'gold',
    icon: <FaHandHoldingUsd />,
    price: { monthly: 30, annual: 288 },
    features: {
      'Acesso ao servidor': true,
      'Pontos de home': { value: '15 homes', has: true },
      'Prefixo no chat': { value: 'Tag [Investidor]', has: true },
      'Cores e formatação no chat': true,
      'Canal VIP': true,
      'Blocos de proteção': { value: '20.000 blocos · 200/h', has: true },
      'Anúncios no leilão': { value: '10 anúncios leilão', has: true },
      'Taxa do banco (PIX)': { value: 'Taxa PIX 0%', has: true },
      'Cofres no banco': { value: '5 cofres · juros +0,5%', has: true },
      'Desconto no baú do clã': { value: '40% no baú do clã', has: true },
      'Gradiente na tag do clã': true,
      'Comandos utilitários': { value: '/craft, /ec e /anvil', has: true },
      'Efeitos de partícula': { value: '5 efeitos', has: true },
      'Salário diário': { value: 'Salário 5.000/dia', has: true },
      'Kit exclusivo': { value: 'Kit semanal premium', has: true },
      'Prioridade na fila': true,
      'Acesso antecipado a eventos': true,
      'Suporte prioritário': true,
    },
    highlighted: false,
  },
];

const VIP = () => {
  const { t, get, localeMeta } = useI18n();
  const faqs = get('vip.faq') || [];
  const featMap = get('vip.feat') || {};
  const valMap = get('vip.val') || {};
  const translateFeat = (name) => featMap[name] || name;
  const translateVal = (value) => valMap[value] || value;

  const [searchParams, setSearchParams] = useSearchParams();
  const [isAnnual, setIsAnnual] = useState(false);
  const [discordUser, setDiscordUser] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [minecraftNick, setMinecraftNick] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [pricing, setPricing] = useState(null);
  const [pricingBusy, setPricingBusy] = useState(false);
  const [pricingError, setPricingError] = useState('');
  /** Preços com promo VIP automática por plano (cards da página). */
  const [planPromos, setPlanPromos] = useState({});
  /** Preços base carregados do backend (editáveis no admin). */
  const [livePrices, setLivePrices] = useState(null);
  /** Vantagens carregadas do backend (editáveis no admin). */
  const [liveFeatures, setLiveFeatures] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState(null);
  const [pixData, setPixData] = useState(null);
  const [pixStatus, setPixStatus] = useState('');
  const [celebration, setCelebration] = useState(null);
  const celebratedRef = useRef(false);

  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  const triggerCelebration = useCallback((planName) => {
    if (celebratedRef.current) return;
    celebratedRef.current = true;
    setCelebration({
      planName: planName || null,
      message: planName
        ? t('vip.celebrateMsgNamed', { plan: planName })
        : t('vip.celebrateMsg'),
    });
    fireVipConfetti();
  }, [t]);

  const closeCelebration = () => {
    setCelebration(null);
    celebratedRef.current = false;
  };

  useEffect(() => {
    setDiscordUser(discordService.getVipAuth());

    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setBanner({
        type: 'success',
        text: t('vip.bannerSuccess'),
      });
      triggerCelebration();
    } else if (checkout === 'cancel') {
      setBanner({
        type: 'cancel',
        text: t('vip.bannerCancel'),
      });
    } else if (checkout === 'ready') {
      setBanner({
        type: 'success',
        text: t('vip.bannerReady'),
      });
    }

    if (checkout) {
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, triggerCelebration, t]);

  useEffect(() => {
    if (!pixData?.paymentId || pixStatus === 'approved') return undefined;
    if (String(pixData.paymentId).startsWith('sim_')) return undefined;

    const tick = async () => {
      try {
        const result = await getPixPaymentStatus(pixData.paymentId);
        setPixStatus(result.status || '');
        if (result.status === 'approved') {
          const planName = checkoutPlan?.id
            ? t(`vip.plans.${checkoutPlan.id}.name`)
            : null;
          setBanner({
            type: 'success',
            text: t('vip.bannerPixOk'),
          });
          setCheckoutPlan(null);
          setPixData(null);
          triggerCelebration(planName);
        }
      } catch {
        // silencia falhas de polling
      }
    };

    tick();
    const id = window.setInterval(tick, 3500);
    return () => window.clearInterval(id);
  }, [pixData, pixStatus, checkoutPlan, triggerCelebration, t]);

  const displayPlans = useMemo(() => {
    return plans.map((plan) => {
      let next = plan;
      if (livePrices) {
        const live = livePrices[plan.id];
        if (live && plan.price.monthly !== 0) {
          next = {
            ...next,
            price: {
              monthly: Number(live.monthly),
              annual: Number(live.annual),
            },
          };
        }
      }
      if (liveFeatures?.plans?.[plan.id]) {
        next = {
          ...next,
          features: liveFeatures.plans[plan.id],
        };
      }
      return next;
    });
  }, [livePrices, liveFeatures]);

  const displayFeatureOrder = useMemo(() => {
    if (
      Array.isArray(liveFeatures?.featureOrder) &&
      liveFeatures.featureOrder.length
    ) {
      return liveFeatures.featureOrder;
    }
    return allFeatures;
  }, [liveFeatures]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchVipPricing();
        if (cancelled) return;
        setLivePrices(data.byId || null);
        setLiveFeatures(data.features || null);
      } catch {
        if (!cancelled) {
          setLivePrices(null);
          setLiveFeatures(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatPrice = (plan) => {
    if (plan.price.monthly === 0) return '0';
    return isAnnual ? Math.round(plan.price.annual / 12) : plan.price.monthly;
  };

  const formatCentsCard = (cents, { annualAsMonthly = false } = {}) => {
    let value = Number(cents || 0) / 100;
    if (annualAsMonthly) value /= 12;
    return value.toLocaleString(localeMeta.dateLocale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  // Promo VIP automática nos cards (sem cupom)
  useEffect(() => {
    let cancelled = false;
    const billing = isAnnual ? 'annual' : 'monthly';
    const paid = plans.filter((p) => p.price.monthly > 0);

    (async () => {
      try {
        const results = await Promise.all(
          paid.map(async (plan) => {
            try {
              const data = await validatePromoCode({
                planId: plan.id,
                billing,
                code: '',
              });
              return [plan.id, data.pricing || null];
            } catch {
              return [plan.id, null];
            }
          })
        );
        if (cancelled) return;
        const next = {};
        for (const [id, pricingRow] of results) {
          if (
            pricingRow?.hasDiscount &&
            pricingRow.source === 'vip_promo'
          ) {
            next[id] = pricingRow;
          }
        }
        setPlanPromos(next);
      } catch {
        if (!cancelled) setPlanPromos({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAnnual, livePrices]);

  const openCheckout = (plan) => {
    setError('');
    if (plan.price.monthly === 0) {
      window.location.href = '/#como-entrar';
      return;
    }
    setCheckoutPlan(plan);
    setPaymentMethod('card');
    setPixData(null);
    setPixStatus('');
    setMinecraftNick((prev) => prev || '');
  };

  const closeModal = () => {
    if (loading) return;
    setCheckoutPlan(null);
    setPixData(null);
    setPixStatus('');
    setError('');
    setPromoCode('');
    setPricing(null);
    setPricingError('');
  };

  const refreshPricing = useCallback(
    async (codeOverride) => {
      if (!checkoutPlan?.id) return;
      const billing = isAnnual ? 'annual' : 'monthly';
      const code =
        codeOverride !== undefined ? codeOverride : promoCode.trim();
      setPricingBusy(true);
      setPricingError('');
      try {
        const data = await validatePromoCode({
          planId: checkoutPlan.id,
          billing,
          code,
        });
        setPricing(data.pricing || null);
      } catch (err) {
        setPricing(null);
        if (code) setPricingError(err.message || t('vip.errInvalidCode'));
        else setPricingError('');
      } finally {
        setPricingBusy(false);
      }
    },
    [checkoutPlan, isAnnual, promoCode, t]
  );

  useEffect(() => {
    if (!checkoutPlan || pixData) return undefined;
    const timeoutId = setTimeout(() => {
      refreshPricing();
    }, promoCode.trim() ? 350 : 0);
    return () => clearTimeout(timeoutId);
  }, [checkoutPlan, isAnnual, promoCode, pixData, refreshPricing]);

  const connectDiscord = () => {
    window.location.href = discordService.getAuthUrl('vip');
  };

  const disconnectDiscord = () => {
    discordService.clearVipAuth();
    setDiscordUser(null);
  };

  const copyPixCode = async () => {
    if (!pixData?.qrCode) return;
    try {
      await navigator.clipboard.writeText(pixData.qrCode);
      setBanner({ type: 'success', text: t('vip.bannerPixCopied') });
    } catch {
      setError(t('vip.errCopy'));
    }
  };

  const runSimulatePixPaid = async () => {
    setError('');
    if (!discordUser?.id) {
      setError(t('vip.errConnectSim'));
      return;
    }
    const nick = minecraftNick.trim();
    if (!isValidMinecraftNick(nick)) {
      setError(t('vip.errNick'));
      return;
    }
    if (!checkoutPlan) return;

    setLoading(true);
    try {
      await simulatePixPayment({
        planId: checkoutPlan.id,
        billing: isAnnual ? 'annual' : 'monthly',
        discordId: discordUser.id,
        minecraftNick: nick,
        paymentId: pixData?.paymentId,
      });
      const translatedName = t(`vip.plans.${checkoutPlan.id}.name`);
      setBanner({
        type: 'success',
        text: t('vip.bannerPixSim'),
      });
      setCheckoutPlan(null);
      setPixData(null);
      setPixStatus('approved');
      triggerCelebration(translatedName);
    } catch (err) {
      setError(err.message || t('vip.errSimPix'));
    } finally {
      setLoading(false);
    }
  };

  const startPayment = async () => {
    setError('');

    if (!discordUser?.id) {
      setError(t('vip.errConnectPay'));
      return;
    }

    const nick = minecraftNick.trim();
    if (!isValidMinecraftNick(nick)) {
      setError(t('vip.errNick'));
      return;
    }

    setLoading(true);
    try {
      const billing = isAnnual ? 'annual' : 'monthly';
      const common = {
        planId: checkoutPlan.id,
        billing,
        discordId: discordUser.id,
        discordUsername: discordUser.username || discordUser.global_name,
        minecraftNick: nick,
        promoCode: promoCode.trim() || undefined,
      };

      if (paymentMethod === 'pix') {
        const data = await createPixPayment(common);
        setPixData(data);
        setPixStatus(data.status || 'pending');
        setLoading(false);
        return;
      }

      const { url } = await createCheckoutSession(common);
      window.location.href = url;
    } catch (err) {
      setError(err.message || t('vip.errStartPay'));
      setLoading(false);
    }
  };

  return (
    <div className="vip">
      <Navbar />

      <section className="vip-hero">
        <div className="vip-hero__glow" aria-hidden="true" />
        <div className="vip-hero__blocks" aria-hidden="true" />
        <div className="vip-wrap vip-hero__content">
          <p className="vip-hero__kicker">{t('vip.kicker')}</p>
          <h1 className="vip-hero__title">{t('vip.title')}</h1>
          <p className="vip-hero__lead">
            {t('vip.lead')}
          </p>

          {banner && (
            <div className={`vip-banner vip-banner--${banner.type}`} role="status">
              {banner.text}
              <button type="button" onClick={() => setBanner(null)} aria-label={t('vip.close')}>
                ×
              </button>
            </div>
          )}

          <div className="vip-toggle" role="group" aria-label={t('vip.billingLabel')}>
            <button
              type="button"
              className={`vip-toggle__btn ${!isAnnual ? 'is-active' : ''}`}
              onClick={() => setIsAnnual(false)}
            >
              {t('vip.monthly')}
            </button>
            <button
              type="button"
              className={`vip-toggle__btn ${isAnnual ? 'is-active' : ''}`}
              onClick={() => setIsAnnual(true)}
            >
              {t('vip.annual')}
              <span className="vip-toggle__save">{t('vip.save20')}</span>
            </button>
          </div>
        </div>
      </section>

      <section className="vip-plans">
        <div className="vip-wrap">
          <div className="vip-plans__grid">
            {displayPlans.map((plan, index) => (
              <article
                key={plan.id}
                className={`vip-card vip-card--${plan.accent} ${plan.highlighted ? 'vip-card--featured' : ''}`}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                {plan.highlighted && <span className="vip-card__badge">{t('vip.mostPopular')}</span>}

                <header className="vip-card__head">
                  <span className="vip-card__icon" aria-hidden="true">
                    {plan.icon}
                  </span>
                  <div>
                    <h2 className="vip-card__name">{t(`vip.plans.${plan.id}.name`)}</h2>
                    <p className="vip-card__tagline">{t(`vip.plans.${plan.id}.tagline`)}</p>
                  </div>
                </header>

                <div className="vip-card__price">
                  {plan.price.monthly === 0 ? (
                    <span className="vip-card__free">{t('vip.free')}</span>
                  ) : planPromos[plan.id]?.hasDiscount ? (
                    <div className="vip-card__price-stack">
                      <span className="vip-card__price-was">
                        R$ {formatPrice(plan)}
                        <span className="vip-card__period">{t('vip.perMonth')}</span>
                      </span>
                      <span className="vip-card__price-now">
                        <span className="vip-card__currency">R$</span>
                        <span className="vip-card__value">
                          {formatCentsCard(planPromos[plan.id].netCents, {
                            annualAsMonthly: isAnnual,
                          })}
                        </span>
                        <span className="vip-card__period">{t('vip.perMonth')}</span>
                      </span>
                    </div>
                  ) : (
                    <>
                      <span className="vip-card__currency">R$</span>
                      <span className="vip-card__value">{formatPrice(plan)}</span>
                      <span className="vip-card__period">{t('vip.perMonth')}</span>
                    </>
                  )}
                </div>

                {planPromos[plan.id]?.hasDiscount ? (
                  <p className="vip-card__promo">
                    {t('vip.promo')}: {planPromos[plan.id].label}
                    {planPromos[plan.id].discountLabel
                      ? ` (−${planPromos[plan.id].discountLabel})`
                      : ''}
                  </p>
                ) : isAnnual && plan.price.annual > 0 ? (
                  <p className="vip-card__yearly">
                    {t('vip.yearlySave', {
                      annual: plan.price.annual,
                      save: plan.price.monthly * 12 - plan.price.annual,
                    })}
                  </p>
                ) : (
                  <p className="vip-card__yearly vip-card__yearly--spacer" aria-hidden="true">
                    &nbsp;
                  </p>
                )}

                {isAnnual &&
                plan.price.annual > 0 &&
                planPromos[plan.id]?.hasDiscount ? (
                  <p className="vip-card__yearly">
                    {t('vip.yearlyNet', {
                      net: formatCentsCard(planPromos[plan.id].netCents),
                    })}
                    {planPromos[plan.id].grossCents > planPromos[plan.id].netCents ? (
                      <>
                        {' '}
                        · {t('vip.yearlyWas')}{' '}
                        <span className="vip-card__price-was-inline">
                          R$ {formatCentsCard(planPromos[plan.id].grossCents)}
                        </span>
                      </>
                    ) : null}
                  </p>
                ) : null}
                <button
                  type="button"
                  className={`vip-card__cta ${plan.highlighted ? 'vip-card__cta--featured' : ''} ${
                    plan.accent === 'gold' ? 'vip-card__cta--gold' : ''
                  }`}
                  onClick={() => openCheckout(plan)}
                >
                  {plan.price.monthly === 0 ? t('vip.playFree') : t('vip.subscribeNow')}
                </button>

                <ul className="vip-card__features">
                  {displayFeatureOrder.map((feature) => {
                    const featureValue = plan.features[feature];
                    const hasFeature =
                      typeof featureValue === 'object' ? featureValue.has : Boolean(featureValue);
                    const displayValue =
                      typeof featureValue === 'object' && featureValue.has
                        ? translateVal(featureValue.value)
                        : translateFeat(feature);

                    return (
                      <li
                        key={feature}
                        className={`vip-card__feature ${hasFeature ? 'is-on' : 'is-off'}`}
                      >
                        {hasFeature ? <FaCheck /> : <FaTimes />}
                        <span>{displayValue}</span>
                      </li>
                    );
                  })}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="vip-faq">
        <div className="vip-wrap">
          <header className="vip-faq__intro">
            <h2>{t('vip.faqTitle')}</h2>
            <p>{t('vip.faqLead')}</p>
          </header>

          <div className="vip-faq__list">
            {faqs.map((item) => (
              <details key={item.q} className="vip-faq__item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="vip-cta">
        <div className="vip-wrap vip-cta__inner">
          <h2>{t('vip.ctaTitle')}</h2>
          <p>{t('vip.ctaLead')}</p>
          <a
            href="https://discord.gg/9W4Vusc4m2"
            target="_blank"
            rel="noopener noreferrer"
            className="vip-btn vip-btn--primary"
          >
            <FaDiscord />
            {t('vip.ctaDiscord')}
          </a>
        </div>
      </section>

      <footer className="vip-footer">
        <div className="vip-wrap vip-footer__top">
          <div className="vip-footer__brand">
            <p className="vip-footer__name">{t('common.brand')}</p>
            <p>{t('vip.footerBrand')}</p>
          </div>
          <div className="vip-footer__cols">
            <div>
              <h4>{t('common.links')}</h4>
              <ul>
                <li><a href="/">{t('nav.home')}</a></li>
                <li><a href="/vip">{t('nav.vip')}</a></li>
                <li><a href="/conta">{t('nav.myAccount')}</a></li>
                <li><a href="/termos">{t('common.terms')}</a></li>
              </ul>
            </div>
            <div>
              <h4>{t('common.community')}</h4>
              <ul>
                <li>
                  <a href="https://discord.gg/9W4Vusc4m2" target="_blank" rel="noopener noreferrer">
                    {t('common.discord')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="vip-footer__bottom">
          <p>{t('common.copyright')}</p>
        </div>
      </footer>

      {celebration && (
        <div
          className="vip-celebrate"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vip-celebrate-title"
        >
          <button
            type="button"
            className="vip-celebrate__backdrop"
            onClick={closeCelebration}
            aria-label={t('vip.celebrateClose')}
          />
          <div className="vip-celebrate__card">
            <span className="vip-celebrate__burst" aria-hidden="true" />
            <p className="vip-celebrate__kicker">{t('vip.celebrateKicker')}</p>
            <h2
              id="vip-celebrate-title"
              className="vip-celebrate__title"
              onClick={() => fireVipConfetti()}
              title={t('vip.celebrateConfetti')}
            >
              {t('vip.celebrateTitle')}
            </h2>
            <p className="vip-celebrate__lead">{celebration.message}</p>
            <p className="vip-celebrate__thanks">
              {t('vip.celebrateThanks')}
            </p>
            <div className="vip-celebrate__actions">
              <a href="/conta" className="vip-btn vip-btn--primary">
                {t('vip.celebrateAccount')}
              </a>
              <button type="button" className="vip-btn vip-btn--ghost" onClick={closeCelebration}>
                {t('vip.celebrateContinue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {checkoutPlan && (
        <div className="vip-modal" role="dialog" aria-modal="true" aria-labelledby="vip-modal-title">
          <button type="button" className="vip-modal__backdrop" onClick={closeModal} aria-label={t('vip.close')} />
          <div className="vip-modal__panel">
            <header className="vip-modal__head">
              <h2 id="vip-modal-title">
                {t('vip.modalSubscribe', {
                  plan: t(`vip.plans.${checkoutPlan.id}.name`),
                })}
              </h2>
              <p>
                {pricing?.hasDiscount ? (
                  <>
                    <span className="vip-modal__price-was">
                      {isAnnual
                        ? t('vip.modalPriceYear', { price: checkoutPlan.price.annual })
                        : t('vip.modalPriceMonth', { price: checkoutPlan.price.monthly })}
                    </span>{' '}
                    <strong className="vip-modal__price-now">{pricing.netLabel}</strong>
                    {isAnnual ? t('vip.perYear') : t('vip.perMonth')}
                  </>
                ) : (
                  <>
                    {isAnnual
                      ? t('vip.modalPriceYear', { price: checkoutPlan.price.annual })
                      : t('vip.modalPriceMonth', { price: checkoutPlan.price.monthly })}
                  </>
                )}
                {paymentMethod === 'pix'
                  ? t('vip.modalPixMethod')
                  : t('vip.modalCardMethod')}
              </p>
              {pricing?.hasDiscount && pricing.label ? (
                <p className="vip-modal__promo-tag">
                  {pricing.source === 'vip_promo' ? t('vip.promo') : t('vip.coupon')}: {pricing.label}
                  {pricing.discountLabel ? ` (−${pricing.discountLabel})` : ''}
                </p>
              ) : null}
            </header>

            <div className="vip-modal__body">
              {!pixData ? (
                <>
                  <div className="vip-modal__block">
                    <span className="vip-modal__label">{t('vip.stepDiscord')}</span>
                    {discordUser ? (
                      <div className="vip-modal__discord">
                        <FaDiscord />
                        <span>
                          {discordUser.global_name || discordUser.username}
                          {discordUser.username ? ` (@${discordUser.username})` : ''}
                        </span>
                        <button type="button" className="vip-modal__link" onClick={disconnectDiscord}>
                          {t('vip.switchAccount')}
                        </button>
                      </div>
                    ) : (
                      <button type="button" className="vip-btn vip-btn--primary" onClick={connectDiscord}>
                        <FaDiscord />
                        {t('vip.connectDiscord')}
                      </button>
                    )}
                  </div>

                  <div className="vip-modal__block">
                    <label className="vip-modal__label" htmlFor="mc-nick">
                      {t('vip.stepNick')}
                    </label>
                    <input
                      id="mc-nick"
                      className="vip-modal__input"
                      type="text"
                      maxLength={16}
                      placeholder={t('vip.nickPlaceholder')}
                      value={minecraftNick}
                      onChange={(e) => setMinecraftNick(e.target.value)}
                      disabled={loading}
                      autoComplete="off"
                    />
                  </div>

                  <div className="vip-modal__block">
                    <label className="vip-modal__label" htmlFor="vip-promo">
                      {t('vip.stepPromo')}
                    </label>
                    <div className="vip-promo-row">
                      <input
                        id="vip-promo"
                        className="vip-modal__input"
                        type="text"
                        maxLength={32}
                        placeholder={t('vip.promoPlaceholder')}
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        disabled={loading || pricingBusy}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="vip-btn vip-btn--ghost vip-promo-row__btn"
                        onClick={() => refreshPricing()}
                        disabled={loading || pricingBusy}
                      >
                        {pricingBusy ? '…' : t('vip.apply')}
                      </button>
                    </div>
                    {pricingError ? (
                      <p className="vip-modal__error">{pricingError}</p>
                    ) : pricing?.creatorName ? (
                      <p className="vip-modal__note">
                        {t('vip.creatorPartner', { name: pricing.creatorName })}
                        {pricing.hasDiscount ? '' : t('vip.creatorNoExtra')}
                      </p>
                    ) : pricing?.source === 'vip_promo' && pricing.hasDiscount ? (
                      <p className="vip-modal__note">
                        {t('vip.autoPromo')}
                      </p>
                    ) : null}
                  </div>

                  <div className="vip-modal__block">
                    <span className="vip-modal__label" id="vip-pay-label">
                      {t('vip.stepPay')}
                    </span>
                    <div className="vip-pay" role="group" aria-labelledby="vip-pay-label">
                      <button
                        type="button"
                        className={`vip-pay__btn ${paymentMethod === 'card' ? 'is-active' : ''}`}
                        onClick={() => setPaymentMethod('card')}
                        disabled={loading}
                      >
                        <FaCreditCard aria-hidden="true" />
                        <span className="vip-pay__title">{t('vip.payCard')}</span>
                        <span className="vip-pay__hint">{t('vip.payCardHint')}</span>
                      </button>
                      <button
                        type="button"
                        className={`vip-pay__btn ${paymentMethod === 'pix' ? 'is-active' : ''}`}
                        onClick={() => setPaymentMethod('pix')}
                        disabled={loading}
                      >
                        <FaQrcode aria-hidden="true" />
                        <span className="vip-pay__title">{t('vip.payPix')}</span>
                        <span className="vip-pay__hint">{t('vip.payPixHint')}</span>
                      </button>
                    </div>
                    {paymentMethod === 'pix' && (
                      <p className="vip-modal__note">
                        {t('vip.pixNote')}
                        {isLocalDev ? t('vip.pixNoteLocal') : ''}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="vip-pix">
                  <p className="vip-pix__status">
                    {pixStatus === 'approved'
                      ? t('vip.pixApproved')
                      : t('vip.pixPending')}
                  </p>
                  {pixData.qrCodeBase64 && (
                    <img
                      className="vip-pix__qr"
                      src={`data:image/png;base64,${pixData.qrCodeBase64}`}
                      alt={t('vip.pixQrAlt')}
                    />
                  )}
                  <div className="vip-pix__copy">
                    <input
                      className="vip-modal__input"
                      type="text"
                      readOnly
                      value={pixData.qrCode}
                      aria-label={t('vip.pixCodeAria')}
                    />
                    <button type="button" className="vip-btn vip-btn--primary" onClick={copyPixCode}>
                      <FaCopy />
                      {t('vip.copy')}
                    </button>
                  </div>
                  <p className="vip-modal__note">
                    {t('vip.pixWaiting')}
                  </p>
                  {isLocalDev && (
                    <button
                      type="button"
                      className="vip-btn vip-btn--simulate"
                      onClick={runSimulatePixPaid}
                      disabled={loading}
                    >
                      {loading ? t('vip.simulating') : t('vip.simLocal')}
                    </button>
                  )}
                </div>
              )}

              {error && <p className="vip-modal__error">{error}</p>}
            </div>

            <footer className="vip-modal__actions">
              <button type="button" className="vip-btn vip-btn--ghost" onClick={closeModal} disabled={loading}>
                {pixData ? t('vip.closeModal') : t('vip.cancel')}
              </button>
              {!pixData && isLocalDev && paymentMethod === 'pix' && (
                <button
                  type="button"
                  className="vip-btn vip-btn--simulate"
                  onClick={runSimulatePixPaid}
                  disabled={loading || !discordUser}
                >
                  {loading ? t('vip.simulating') : t('vip.simLocalShort')}
                </button>
              )}
              {!pixData && (
                <button
                  type="button"
                  className="vip-btn vip-btn--primary"
                  onClick={startPayment}
                  disabled={loading || !discordUser}
                >
                  {loading
                    ? paymentMethod === 'pix'
                      ? t('vip.generatingPix')
                      : t('vip.redirecting')
                    : paymentMethod === 'pix'
                      ? t('vip.generatePix')
                      : t('vip.goPay')}
                </button>
              )}
            </footer>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <SupportWidget oauthIntent="vip" />
      </Suspense>
    </div>
  );
};

export default VIP;
